/**
 * User store — single source of truth for all user, balance, and transaction data.
 * Populated entirely by the SignalR WebSocket events (Dashboard, Balance, User, Noti).
 * Data lives in memory only — always fresh from the backend, never cached.
 *
 * Ported from V1 with the same logic and normalization functions.
 */

import { create } from 'zustand'
import { produce } from 'immer'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Address {
  id: number
  street: string
  streetNumber?: string | null
  city: string
  state: string
  countryId: number
  zip: string
}

export interface Email {
  id: number
  email: string
  isPrimary?: boolean
}

export interface Phone {
  id: number
  phoneNumber: string
  isPrimary?: boolean
}

export interface Owner {
  clientId: number | string
  personType: number
  personName: string
  personIdTypes: number
  personId: string
}

export interface RecentContact {
  transferCode: string
  transferAlias: string
  bank: string
  currency: number | string
  owner: Owner
  isActive: boolean
  isInternal: boolean
}

export interface LastMovement {
  id: number
  amount: number
  displayName: string
  description: string
  accountingDate: string
  movementTypeDescription: string
  ticketStatus: string
}

export interface TicketMovement {
  Id: number
  Amount: number
  DisplayName: string
  Description: string
  AccountingDate: string
  MovementTypeDescription: string
  TicketStatus: string | null
}

export interface Ticket {
  Id: number
  TicketTypeId: number
  TicketStatusId: number
  BalanceId: number
  TransferConceptId: number
  Conciliation: string
  Amount: number
  Date: string
  ExternalId: string | null
  OriginatorName: string
  OriginatorIdentifier: string
  OriginatorBank: string
  OriginatorAccountNumber: string
  OriginatorAlias: string
  DestinationName: string
  DestinationIdentifier: string
  DestinationBank: string
  DestinationAccountNumber: string
  DestinationAlias: string
  Metadata: string
  CreatedById: number
  ChargesMoved: boolean
  Movements: TicketMovement[]
}

export interface Balance {
  id: number
  profileId: number
  canTransfer?: boolean
  taxName: string
  displayName: string
  creationDate: string
  currencyId: number
  transferCode: string
  transferAlias: string
  balanceAmount: number
  ticketStatus?: string
  lastMovements: LastMovement[]
  recentContacts: RecentContact[]
  tickets?: Ticket[]
  profilePicture?: string | null
  QrEnabled: boolean
}

export interface Notification {
  id: number
  title: string
  message: string
  date: string
  pending: boolean
}

export interface TransferConcept { id: number; description: string }
export interface Countries        { id: number; description: string }
export interface States           { id: number; description: string }
export interface Currencies       { id: number; description: string }
export interface Roles            { id: number; description: string }
export interface TicketStatus     { id: number; description: string }
export interface MovementType     { id: number; description: string; code: string }
export interface TicketTypes      { Id: number; Description: string }

export interface MasterData {
  transferMotives: TransferConcept[]
  states:          States[]
  countries:       Countries[]
  currencies:      Currencies[]
  roles:           Roles[]
  ticketStatus:    TicketStatus[]
  movementTypes:   MovementType[]
  ticketTypes:     TicketTypes[]
}

export interface User {
  id: number
  name: string
  lastname: string
  documentNumber: string
  birthDate?: string
  email: Email
  address: Address
  phone: Phone
  taxTypeId?: number
  lastLogin?: string
  userTypeId?: number
  profilesId?: number[]
  balances: Balance[]
  rolesId?: string
  profileTypeId?: number
  photo?: string
  notifications: Notification[]
  masterData: MasterData
}

interface Account {
  cvu:   string
  alias: string
}

export interface PendingTransfer {
  ticketId:      string
  amount:        number
  recipientName: string
  initiatedAt:   string
}

export interface UserState {
  user:              User
  account:           Account
  selectedBalanceId?: number
  isLoadedData?:     boolean
  isConnecting?:     boolean
  pendingTransfers:  PendingTransfer[]
}

interface Actions {
  // getters
  getUser:               () => User
  getSelectedBalanceId:  () => number | undefined
  getQrEnabled:          (balanceId?: number) => boolean

  // flags
  setIsLoadedData:   (status: boolean) => void
  setIsConnecting:   (status: boolean) => void

  // setters
  setSelectedBalance: (balanceId: number) => void

  // data loads / updates
  setMasterData:      (data: unknown) => void
  setUserData:        (data: unknown) => void
  updateUserData:     (data: unknown) => void
  updateBalanceData:  (data: unknown) => void
  updateNotifications:(data: unknown) => void
  /** Marca notificaciones como leídas (pending=false) y lo persiste. */
  markNotificationsRead: (ids: number[]) => void
  /** Revierte el marcado (pending=true) y lo quita del registro local. */
  markNotificationsUnread: (ids: number[]) => void

  // verification / profile mutations
  setEmail:           (email: string) => void
  setPhone:            (phone: string) => void
  setAddress:          (addr: Partial<Address>) => void
  setTransferAlias:    (balanceId: number, alias: string) => void
  removeBalance:       (balanceId: number) => void

  // pending transfers
  addPendingTransfer:   (transfer: PendingTransfer) => void
  clearPendingTransfers: () => void

  // cleanup
  clearUserData: () => Promise<void>
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const toArray = <T>(x: T | T[] | null | undefined): T[] =>
  Array.isArray(x) ? x : x ? [x] : []

const iso = (d?: string | Date | null) =>
  d ? new Date(d).toISOString() : new Date(0).toISOString()

const uniqueBy = <T, K>(items: T[], key: (i: T) => K): T[] => {
  const seen = new Set<K>()
  return items.filter(it => {
    const k = key(it)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

const sortByDateDesc = <T>(items: T[], getDate: (i: T) => string) =>
  [...items].sort(
    (a, b) => new Date(getDate(b)).getTime() - new Date(getDate(a)).getTime()
  )

const mapTicketStatusId = (id?: number): string => {
  switch (id) {
    case 4:  return 'Confirmado'
    case 5:  return 'Pendiente'
    default: return 'Desconocido'
  }
}

// ---------------------------------------------------------------------------
// Raw API payload shapes (PascalCase from backend)
// ---------------------------------------------------------------------------

interface RawNotification { Id?: number; Title?: string; Message?: string; Date?: string; Pending?: boolean }
interface RawOwner { ClientId?: number | string; PersonType?: number; PersonName?: string; PersonIdType?: number; PersonId?: string }
interface RawRecentContact { TransferCode?: string; TransferAlias?: string; Bank?: string; Currency?: number | string; Owner?: RawOwner; IsActive?: boolean; IsInternal?: boolean }
interface RawTicketMovement { Id?: number; Amount?: number; DisplayName?: string; Description?: string; AccountingDate?: string; MovementTypeDescription?: string; TicketStatus?: string | null }
interface RawTicket { Id?: number; TicketTypeId?: number; TicketStatusId?: number; BalanceId?: number; TransferConceptId?: number; Conciliation?: string; Amount?: number; Date?: string; ExternalId?: string | null; OriginatorName?: string; OriginatorIdentifier?: string; OriginatorBank?: string; OriginatorAccountNumber?: string; OriginatorAlias?: string; DestinationName?: string; DestinationIdentifier?: string; DestinationBank?: string; DestinationAccountNumber?: string; DestinationAlias?: string; Metadata?: string; CreatedById?: number; ChargesMoved?: boolean; Movements?: RawTicketMovement[] }
interface RawBalance { Id?: number; ProfileId?: number; TaxName?: string; DisplayName?: string; CreationDate?: string; CanTransfer?: boolean; CurrencyId?: number; TransferCode?: string; TransferAlias?: string; BalanceAmount?: number; Tickets?: RawTicket[]; RecentContacts?: RawRecentContact[]; ProfilePicture?: string | null; QrEnabled?: boolean }
interface RawMasterDataEntity { Id?: number; Description?: string; Code?: string }
interface RawMasterData { TransferMotives?: RawMasterDataEntity[]; States?: RawMasterDataEntity[]; Countries?: RawMasterDataEntity[]; Currencies?: RawMasterDataEntity[]; Roles?: RawMasterDataEntity[]; TicketStatus?: RawMasterDataEntity[]; MovementTypes?: RawMasterDataEntity[]; TicketTypes?: RawMasterDataEntity[] }
interface RawEmail   { Id?: number; Email?: string; IsPrimary?: boolean }
interface RawPhone   { Id?: number; PhoneNumber?: string; IsPrimary?: boolean }
interface RawAddress { Id?: number; Street?: string; StreetNumber?: string | null; City?: string; State?: string; CountryId?: number; PostalCode?: string | number }
interface RawUserObj { Id?: number; Name?: string; LastName?: string; DocumentNumber?: string | number; BirthDate?: string; Email?: RawEmail; Address?: RawAddress; Phone?: RawPhone; RolesId?: string; ProfileTypeId?: number; UserPicture?: string }
interface RawUserData { User?: RawUserObj; TaxType?: number; LastLogin?: string; UserTypeId?: number; ProfilesId?: number[]; Balances?: RawBalance[]; Notifications?: RawNotification[]; MasterData?: RawMasterData }

// ---------------------------------------------------------------------------
// Normalizers — convert raw PascalCase API payloads to camelCase store models
// ---------------------------------------------------------------------------

function normalizeNotifications(raw: unknown): Notification[] {
  return toArray(raw as RawNotification).map((n): Notification => ({
    id:      n.Id      ?? 0,
    title:   n.Title   ?? '',
    message: n.Message ?? '',
    date:    iso(n.Date),
    pending: !!n.Pending,
  }))
}

function normalizeRecentContacts(raw: unknown): RecentContact[] {
  return toArray(raw as RawRecentContact).map((c): RecentContact => ({
    transferCode:  c.TransferCode  ?? '',
    transferAlias: c.TransferAlias ?? '',
    bank:          c.Bank          ?? '',
    currency:      c.Currency      ?? '',
    owner: {
      clientId:    c.Owner?.ClientId   ?? 0,
      personType:  c.Owner?.PersonType ?? 0,
      personName:  c.Owner?.PersonName ?? '',
      personIdTypes: c.Owner?.PersonIdType ?? 0,
      personId:    c.Owner?.PersonId   ?? '',
    },
    isActive:   !!c.IsActive,
    isInternal: !!c.IsInternal,
  }))
}

function normalizeTickets(raw: unknown): Ticket[] {
  return toArray(raw as RawTicket).map((t): Ticket => ({
    Id:                     t.Id                     ?? 0,
    TicketTypeId:           t.TicketTypeId           ?? 0,
    TicketStatusId:         t.TicketStatusId         ?? 0,
    BalanceId:              t.BalanceId              ?? 0,
    TransferConceptId:      t.TransferConceptId      ?? 0,
    Conciliation:           t.Conciliation           ?? '',
    Amount:                 t.Amount                 ?? 0,
    Date:                   iso(t.Date),
    ExternalId:             t.ExternalId             ?? null,
    OriginatorName:         t.OriginatorName         ?? '',
    OriginatorIdentifier:   t.OriginatorIdentifier   ?? '',
    OriginatorBank:         t.OriginatorBank         ?? '',
    OriginatorAccountNumber:t.OriginatorAccountNumber ?? '',
    OriginatorAlias:        t.OriginatorAlias        ?? '',
    DestinationName:        t.DestinationName        ?? '',
    DestinationIdentifier:  t.DestinationIdentifier  ?? '',
    DestinationBank:        t.DestinationBank        ?? '',
    DestinationAccountNumber:t.DestinationAccountNumber ?? '',
    DestinationAlias:       t.DestinationAlias       ?? '',
    Metadata:               t.Metadata               ?? '',
    CreatedById:            t.CreatedById            ?? 0,
    ChargesMoved:           !!t.ChargesMoved,
    Movements: toArray(t.Movements as RawTicketMovement[]).map((m): TicketMovement => ({
      Id:                      m.Id                      ?? 0,
      Amount:                  m.Amount                  ?? 0,
      DisplayName:             m.DisplayName             ?? '',
      Description:             m.Description             ?? '',
      AccountingDate:          iso(m.AccountingDate),
      MovementTypeDescription: m.MovementTypeDescription ?? '',
      TicketStatus:            m.TicketStatus            ?? null,
    })),
  }))
}

function normalizeMovementsFromTickets(tickets: Ticket[]): LastMovement[] {
  const all: LastMovement[] = tickets.flatMap(t =>
    toArray(t.Movements).map((m): LastMovement => ({
      id:                      m.Id    ?? 0,
      amount:                  m.Amount ?? 0,
      displayName:             m.DisplayName ?? '',
      description:             m.Description ?? '',
      accountingDate:          iso(m.AccountingDate),
      movementTypeDescription: m.MovementTypeDescription ?? '',
      ticketStatus:            (m.TicketStatus ?? mapTicketStatusId(t.TicketStatusId)) || '',
    }))
  )
  return sortByDateDesc(
    uniqueBy(all, x => x.id || `${x.displayName}-${x.accountingDate}`),
    x => x.accountingDate
  )
}

function normalizeBalances(raw: unknown): Balance[] {
  return toArray(raw as RawBalance).map((b): Balance => {
    const tickets       = normalizeTickets(b.Tickets)
    const lastMovements = normalizeMovementsFromTickets(tickets)
    const recentContacts = normalizeRecentContacts(b.RecentContacts)
    return {
      id:            b.Id            ?? 0,
      profileId:     b.ProfileId     ?? 0,
      taxName:       b.TaxName       ?? '',
      displayName:   b.DisplayName   ?? '',
      creationDate:  iso(b.CreationDate),
      canTransfer:   !!b.CanTransfer,
      currencyId:    b.CurrencyId    ?? 0,
      transferCode:  b.TransferCode  ?? '',
      transferAlias: b.TransferAlias ?? '',
      balanceAmount: b.BalanceAmount ?? 0,
      lastMovements,
      recentContacts,
      tickets,
      profilePicture: b.ProfilePicture ?? null,
      QrEnabled:      !!b.QrEnabled,
    }
  })
}

const toEntity = (e: RawMasterDataEntity) => ({ id: e?.Id ?? 0, description: e?.Description ?? '' })

function normalizeMasterData(raw: RawMasterData): MasterData {
  return {
    transferMotives: toArray(raw?.TransferMotives).map(toEntity),
    states:          toArray(raw?.States).map(toEntity),
    countries:       toArray(raw?.Countries).map(toEntity),
    currencies:      toArray(raw?.Currencies).map(toEntity),
    roles:           toArray(raw?.Roles).map(toEntity),
    ticketStatus:    toArray(raw?.TicketStatus).map(toEntity),
    movementTypes:   toArray(raw?.MovementTypes).map(m => ({ id: m?.Id ?? 0, description: m?.Description ?? '', code: m?.Code ?? '' })),
    ticketTypes:     toArray(raw?.TicketTypes).map(t => ({ Id: t?.Id ?? 0, Description: t?.Description ?? '' })),
  }
}

function normalizeUser(raw: RawUserData): User {
  return {
    id:             raw?.User?.Id          ?? 0,
    name:           raw?.User?.Name        ?? '',
    lastname:       raw?.User?.LastName    ?? '',
    documentNumber: (raw?.User?.DocumentNumber ?? '').toString(),
    birthDate:      raw?.User?.BirthDate   ?? '',
    email: {
      id:        raw?.User?.Email?.Id        ?? 0,
      email:     raw?.User?.Email?.Email     ?? '',
      isPrimary: !!raw?.User?.Email?.IsPrimary,
    },
    address: {
      id:           raw?.User?.Address?.Id           ?? 0,
      street:       raw?.User?.Address?.Street       ?? '',
      streetNumber: raw?.User?.Address?.StreetNumber ?? null,
      city:         raw?.User?.Address?.City         ?? '',
      state:        raw?.User?.Address?.State        ?? '',
      countryId:    raw?.User?.Address?.CountryId    ?? 0,
      zip:          (raw?.User?.Address?.PostalCode  ?? '').toString().trim(),
    },
    phone: {
      id:          raw?.User?.Phone?.Id          ?? 0,
      phoneNumber: raw?.User?.Phone?.PhoneNumber ?? '',
      isPrimary:   !!raw?.User?.Phone?.IsPrimary,
    },
    taxTypeId:    raw?.TaxType        ?? undefined,
    lastLogin:    raw?.LastLogin      ?? undefined,
    userTypeId:   raw?.UserTypeId     ?? undefined,
    profilesId:   raw?.ProfilesId     ?? [],
    balances:     normalizeBalances(raw?.Balances),
    rolesId:      raw?.User?.RolesId  ?? '',
    profileTypeId: raw?.User?.ProfileTypeId ?? undefined,
    photo:        raw?.User?.UserPicture    ?? '',
    notifications: normalizeNotifications(raw?.Notifications),
    masterData:   normalizeMasterData(raw?.MasterData ?? {}),
  }
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const EMPTY_MASTER_DATA: MasterData = {
  transferMotives: [], states: [], countries: [], currencies: [],
  roles: [], ticketStatus: [], movementTypes: [], ticketTypes: [],
}

const initialState: UserState = {
  user: {
    id: 0, name: '', lastname: '', documentNumber: '', rolesId: '', birthDate: '',
    email:   { id: 0, email: '' },
    address: { id: 0, street: '', city: '', state: '', countryId: 0, zip: '' },
    phone:   { id: 0, phoneNumber: '' },
    balances: [], notifications: [], masterData: EMPTY_MASTER_DATA,
  },
  account:          { cvu: '', alias: '' },
  isLoadedData:     false,
  isConnecting:     false,
  pendingTransfers: [],
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useUserStore = create<UserState & Actions>(
  (set, get) => ({
    ...initialState,

      /* Getters */
      getUser:              () => get().user,
      getSelectedBalanceId: () => get().selectedBalanceId,
      getQrEnabled: (balanceId?: number) => {
        const { user, selectedBalanceId } = get()
        const id = balanceId ?? selectedBalanceId ?? user.balances[0]?.id
        if (!id) return false
        return user.balances.find(b => b.id === id)?.QrEnabled ?? false
      },

      /* Flags */
      setIsLoadedData: (status) => set({ isLoadedData: status }),
      setIsConnecting: (status) => set({ isConnecting: status }),

      /* Setters */
      setSelectedBalance: (balanceId) => set({ selectedBalanceId: balanceId }),

      /* MasterData */
      setMasterData: (data) => {
        try {
          const raw = typeof data === 'string' ? JSON.parse(data) : data
          set(produce((s: UserState) => {
            s.user.masterData = normalizeMasterData((raw ?? {}) as RawMasterData)
          }))
        } catch (e) {
          console.error('[UserStore] setMasterData error:', e)
        }
      },

      /* Full user payload from Dashboard event */
      setUserData: (data) => {
        try {
          const raw = typeof data === 'string' ? JSON.parse(data) : data
          const user = normalizeUser(raw as RawUserData)
          // Preserve existing masterData if Dashboard payload doesn't include it
          const currentMD = get().user.masterData
          const hasMD = raw?.MasterData && Object.keys(raw.MasterData).length > 0
          if (!hasMD && currentMD.roles.length > 0) {
            user.masterData = currentMD
          }
          // Merge incoming notifications with existing ones so that read notifications
          // are never discarded (backend only sends pending ones on reconnect).
          const existing = get().user.notifications
          user.notifications = uniqueBy([...user.notifications, ...existing], n => n.id)
          set({ user })
        } catch (e) {
          console.error('[UserStore] setUserData error:', e)
        }
      },

      /* Partial update from User event */
      updateUserData: (data) => {
        try {
          const raw: RawUserData = typeof data === 'string' ? JSON.parse(data) : data as RawUserData
          set(produce((s: UserState) => {
            if (raw?.User) {
              const u = raw.User
              s.user.id           = u.Id           ?? s.user.id
              s.user.name         = u.Name         ?? s.user.name
              s.user.lastname     = u.LastName     ?? s.user.lastname
              s.user.documentNumber = String(u.DocumentNumber ?? s.user.documentNumber)
            }
            if (raw?.Balances) {
              const incoming = normalizeBalances(raw.Balances)
              for (const nb of incoming) {
                const idx = s.user.balances.findIndex(b => b.id === nb.id)
                if (idx === -1) {
                  s.user.balances.push(nb)
                } else {
                  const cur = s.user.balances[idx]
                  s.user.balances[idx] = {
                    ...cur, ...nb,
                    lastMovements: sortByDateDesc(
                      uniqueBy([...nb.lastMovements, ...cur.lastMovements], m => m.id),
                      m => m.accountingDate
                    ),
                    recentContacts: uniqueBy(
                      [...nb.recentContacts, ...cur.recentContacts],
                      c => c.transferCode
                    ).slice(0, 5),
                    tickets: (nb.tickets?.length) ? nb.tickets : cur.tickets,
                  }
                }
              }
            }
            if (raw?.Notifications) {
              const inc = normalizeNotifications(raw.Notifications)
              s.user.notifications = uniqueBy([...inc, ...s.user.notifications], n => n.id)
            }
          }))
        } catch (e) {
          console.error('[UserStore] updateUserData error:', e)
        }
      },

      /* Single balance update from Balance event */
      updateBalanceData: (data) => {
        try {
          const raw = typeof data === 'string' ? JSON.parse(data) : data
          const nb = normalizeBalances([raw])[0]
          if (!nb?.id) return
          set(produce((s: UserState) => {
            const idx = s.user.balances.findIndex(b => b.id === nb.id)
            if (idx === -1) { s.user.balances.push(nb); return }
            const cur = s.user.balances[idx]
            s.user.balances[idx] = {
              ...cur, ...nb,
              lastMovements: sortByDateDesc(
                uniqueBy([...nb.lastMovements, ...cur.lastMovements], m => m.id),
                m => m.accountingDate
              ),
              recentContacts: uniqueBy(
                [...nb.recentContacts, ...cur.recentContacts],
                c => c.transferCode
              ).slice(0, 5),
              tickets: sortByDateDesc(
                uniqueBy([...(nb.tickets ?? []), ...(cur.tickets ?? [])], t => t.Id),
                t => t.Date
              ),
            }
          }))
        } catch (e) {
          console.error('[UserStore] updateBalanceData error:', e)
        }
      },

      /* Prepend new notifications */
      updateNotifications: (data) => {
        try {
          const inc = normalizeNotifications(typeof data === 'string' ? JSON.parse(data) : data)
          set(produce((s: UserState) => {
            s.user.notifications = uniqueBy([...inc, ...s.user.notifications], n => n.id)
          }))
        } catch (e) {
          console.error('[UserStore] updateNotifications error:', e)
        }
      },

      markNotificationsRead: (ids) => {
        set(produce((s: UserState) => {
          const idSet = new Set(ids)
          s.user.notifications = s.user.notifications.map(n =>
            idSet.has(n.id) ? { ...n, pending: false } : n
          )
        }))
      },

      markNotificationsUnread: (ids) => {
        set(produce((s: UserState) => {
          const idSet = new Set(ids)
          s.user.notifications = s.user.notifications.map(n =>
            idSet.has(n.id) ? { ...n, pending: true } : n
          )
        }))
      },


      setEmail: (email) => set(produce((s: UserState) => {
        s.user.email.email = email
      })),

      setPhone: (phone) => set(produce((s: UserState) => {
        s.user.phone.phoneNumber = phone
      })),

      setAddress: (addr) => set(produce((s: UserState) => {
        Object.assign(s.user.address, addr)
      })),

      setTransferAlias: (balanceId, alias) => set(produce((s: UserState) => {
        const bal = s.user.balances.find(b => b.id === balanceId)
        if (bal) bal.transferAlias = alias
      })),

      removeBalance: (balanceId) => set(produce((s: UserState) => {
        s.user.balances = s.user.balances.filter(b => b.id !== balanceId)
      })),

      addPendingTransfer: (transfer) => set(produce((s: UserState) => {
        if (!s.pendingTransfers.some(p => p.ticketId === transfer.ticketId)) {
          s.pendingTransfers.push(transfer)
        }
      })),

      clearPendingTransfers: () => set({ pendingTransfers: [] }),

      clearUserData: async () => set({ ...initialState }),
    })
)
